import type { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { useCustomerStore } from '@entities/customer'
import { useOptionsStore } from '@shared/model'
import { trackEvent } from '@shared/lib/amplitude'

type UseDisciplineLinkOptions = {
  discipline: Discipline
  isDiscipline: ComputedRef<boolean>
  isRouteDiscipline: ComputedRef<boolean>
  homeRoute: ComputedRef<string | null>
  trackName: string
  text: string
}

type UseDisciplineLinkReturn = {
  discount: ComputedRef<number>
  setRedirect: () => Promise<void>
  handleClick: (event: MouseEvent) => void
}

export const useDisciplineLink = ({
  discipline,
  isDiscipline,
  isRouteDiscipline,
  homeRoute,
  trackName,
  text,
}: UseDisciplineLinkOptions): UseDisciplineLinkReturn => {
  const customerStore = useCustomerStore()
  const optionsStore = useOptionsStore()

  const disciplineData = computed(() =>
    customerStore.getActiveKidDisciplines.find(d => d.code === discipline) || null,
  )

  const discount = computed(() =>
    ((!isDiscipline.value || (disciplineData.value != null && !disciplineData.value.demo?.id && !disciplineData.value.is_paid)) && customerStore.isDeal)
      ? optionsStore.getUpsellDiscount
      : 0,
  )

  const setRedirect = async (): Promise<void> => {
    if (!homeRoute.value) return

    trackEvent('Link_click', { feature: 'navigation', name: trackName, text, url: homeRoute.value })

    await navigateTo(homeRoute.value)
  }

  // The item links to the discipline home: route in-app, and ignore the current discipline, as its disabled item did.
  const handleClick = (event: MouseEvent): void => {
    event.preventDefault()
    if (isRouteDiscipline.value) return

    void setRedirect()
  }

  return {
    discount,
    setRedirect,
    handleClick,
  }
}
